// ─────────────────────────────────────────────────────────────────────────────
// ReportsLandingPage.jsx — dedicated page for the Assessment Reports gallery
// Route: /reports
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";

const N="#0a1628",W="#ffffff",B="#e2ddd6",MU="#7a8398",SL="#475569";
const GOLD="#c8964e",BLUE="#1d4ed8",GREEN="#059669",PUR="#7c3aed",TEAL="#0891b2";
const PHONE_D = "+91 76767 09796";
const WA_URL = `https://wa.me/917676709796?text=Hi%2C%20I%27m%20interested%20in%20AXIS360%20Assess.%20Can%20you%20share%20more%20details%3F`;
const DOMAIN = "https://assess.nonverbals.com";

const REPORTS_GALLERY = [
  { name:"ASSESS v2 \u2014 35-page Report", icon:"\u2726", color:GOLD,
    contains:"Competency radar across 9 domains \u00b7 9-Box placement \u00b7 Domain deep-dives \u00b7 AI narrative \u00b7 IDP linkage \u00b7 Strengths/risks \u00b7 Interview questions" },
  { name:"Group Report \u2014 22 Pages", icon:"\u2b21", color:BLUE,
    contains:"Team overview \u00b7 Competency heatmap \u00b7 Distribution charts \u00b7 Bench strength \u00b7 Norm comparison \u00b7 Hiring implications \u00b7 9-Box \u00b7 Individual cards" },
  { name:"360\u00b0 Feedback Report", icon:"\u21bb", color:PUR,
    contains:"Multi-rater competency scores \u00b7 Self vs others gap \u00b7 Behavioural indicators \u00b7 Development priorities \u00b7 Blind spots identification" },
  { name:"Management SJT Report", icon:"\ud83d\udcbc", color:PUR,
    contains:"8-dimension analysis \u00b7 CHRO lens (risk if low/high) \u00b7 Readiness matrix \u00b7 Benchmark vs 3 management levels \u00b7 Structured interview guide (3Q per dimension)" },
  { name:"Sales SJT Report", icon:"\ud83d\udcc8", color:TEAL,
    contains:"Sales DNA archetype \u00b7 6-dimension breakdown \u00b7 Sector benchmarks (BFSI/Pharma/FMCG) \u00b7 30-60-90 onboarding brief \u00b7 Interview guide" },
  { name:"Numerical Reasoning Report", icon:"\ud83d\udd22", color:BLUE,
    contains:"Percentile gauge \u00b7 4 competency sub-scores \u00b7 Sector benchmarks \u00b7 Item-level review \u00b7 Interview probes \u00b7 Hiring recommendation" },
  { name:"Verbal Reasoning Report", icon:"\ud83d\udcdd", color:PUR,
    contains:"Response pattern donut \u00b7 Error type analysis (True\u2192False, CS\u2192True) \u00b7 Bias detection \u00b7 3 sub-scores \u00b7 Sector benchmarks" },
  { name:"Work Style Screener Report", icon:"\u26a1", color:GREEN,
    contains:"5 traffic lights at a glance \u00b7 Role fit matrix (Warehousing/Retail/Security/Manufacturing) \u00b7 Flagged items \u00b7 Shortlist guidance" },
  { name:"Resilience Screener Report", icon:"\ud83d\udcaa", color:PUR,
    contains:"Circular ring gauge \u00b7 5-dimension pentagon radar \u00b7 Sector risk assessment \u00b7 Flagged low-scored statements \u00b7 Interview probes" },
  { name:"Integrity Screener Report", icon:"\ud83d\udd12", color:BLUE,
    contains:"Risk score meter \u00b7 Flag indicators by severity (critical/high/moderate) \u00b7 Legal disclaimer \u00b7 Interview probes by dimension" },
];

const WHY = "A psychometrically valid score is worthless to a hiring manager who can't act on it. Most assessment vendors hand back a raw score table and leave interpretation to the buyer \u2014 the actual decision-making burden simply moves from the vendor to an untrained line manager. Every AXIS360 report is designed backwards from the decision it needs to support: a hire/no-hire call, a coaching conversation, or a succession placement, not just a number.";

export default function ReportsLandingPage({ onBack, onEnter }){
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Assessment Reports \u2014 AXIS360 | NonVerbals Consulting";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name","description"); document.head.appendChild(meta); }
    meta.setAttribute("content", "Every AXIS360 report, from 35-page competency reports to instant traffic-light screener outputs, built for HR teams and line managers to act on.");

    const scriptId = "reports-page-schema";
    let script = document.getElementById(scriptId);
    if (!script) { script = document.createElement("script"); script.id = scriptId; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      "@context":"https://schema.org", "@type":"Service",
      "name":"AXIS360 Assessment Reports",
      "description":"A gallery of decision-ready assessment report formats spanning competency, screening, SJT and 360-degree feedback outputs.",
      "provider": { "@type":"Organization", "name":"NonVerbals Consulting", "url":"https://nonverbals.com" },
      "url": `${DOMAIN}/reports`,
    });

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',sans-serif",background:W,color:N,minHeight:"100vh",
      overflowX:"hidden",width:"100%",boxSizing:"border-box"}}>
      <style>{`
        * { box-sizing: border-box; }
        @media (max-width: 640px) {
          .rlp-nav-back-label { display:none; }
          .rlp-hero-pad { padding-top:36px !important; padding-bottom:28px !important; }
          .rlp-section-pad { padding-bottom:32px !important; }
          .rlp-cta-btn { width:100%; text-align:center; }
        }
      `}</style>

      <div style={{position:"sticky",top:0,zIndex:50,background:W,borderBottom:`1px solid ${B}`,padding:"12px 16px"}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
          <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",
            display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:N,fontFamily:"inherit",flexShrink:0}}>
            <span style={{fontFamily:"Fraunces,serif",fontSize:18}}>AXIS<span style={{color:GOLD}}>360</span></span>
          </button>
          <div style={{display:"flex",gap:12,alignItems:"center",minWidth:0}}>
            <button onClick={onBack} className="rlp-nav-back-label" style={{background:"none",border:"none",cursor:"pointer",
              fontSize:12,color:SL,fontFamily:"inherit",whiteSpace:"nowrap"}}>{"\u2190 Home"}</button>
            <button onClick={onEnter} style={{background:N,color:W,border:"none",borderRadius:9,
              padding:"9px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>
              Get Started
            </button>
          </div>
        </div>
      </div>

      <div className="rlp-hero-pad" style={{background:`linear-gradient(180deg,${GOLD}08 0%,${W} 60%)`,padding:"56px 20px 40px"}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
            <span style={{fontSize:26}}>{"\ud83d\udcc4"}</span>
            <span style={{fontSize:9,fontWeight:800,color:GOLD,letterSpacing:2.5,
              textTransform:"uppercase",background:GOLD+"14",padding:"4px 12px",borderRadius:99}}>
              AXIS360 Reports
            </span>
          </div>
          <h1 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(28px,6vw,42px)",fontWeight:400,lineHeight:1.15,
            letterSpacing:-0.5,marginBottom:16,color:N,wordBreak:"break-word"}}>
            Reports your stakeholders will actually use.
          </h1>
          <p style={{fontSize:"clamp(14px,3vw,16px)",color:SL,marginBottom:20,fontWeight:500}}>
            From 35-page editorial competency reports to instant traffic-light screener outputs.
          </p>
          <p style={{fontSize:"clamp(13px,2.5vw,14px)",color:SL,lineHeight:1.75,maxWidth:760,marginBottom:28}}>
            {"Every AXIS360 report is designed for HR professionals and line managers to act on immediately \u2014 not for a psychometrician to interpret. Ten distinct report formats, each built around the specific decision it needs to support."}
          </p>
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <button onClick={onEnter} className="rlp-cta-btn" style={{background:N,color:W,border:"none",borderRadius:10,
              padding:"13px 26px",fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              {"Start Free Trial \u2192"}
            </button>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="rlp-cta-btn" style={{background:W,color:N,
              border:`1.5px solid ${B}`,borderRadius:10,padding:"13px 26px",fontSize:13.5,fontWeight:700,
              cursor:"pointer",fontFamily:"inherit",textDecoration:"none",display:"inline-block"}}>
              {"\ud83d\udcac Talk to Us \u2014 "}{PHONE_D}
            </a>
          </div>
        </div>
      </div>

      {/* WHY */}
      <div className="rlp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 48px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <span style={{fontSize:9,fontWeight:800,color:"#dc2626",letterSpacing:2.5,textTransform:"uppercase",
            background:"#fef2f2",padding:"4px 12px",borderRadius:99}}>Why</span>
          <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,margin:0}}>
            Why report design matters
          </h2>
        </div>
        <div style={{display:"flex",gap:14,alignItems:"flex-start",background:"#fef9f0",
          border:"1px solid #fde68a",borderRadius:14,padding:"20px 18px",flexWrap:"wrap"}}>
          <svg width="40" height="40" viewBox="0 0 40 40" style={{flexShrink:0}}>
            <circle cx="20" cy="20" r="18" fill="#fef3c7" stroke="#d97706" strokeWidth="1.5"/>
            <text x="20" y="27" textAnchor="middle" fontSize="20" fill="#d97706">!</text>
          </svg>
          <div style={{fontSize:14,color:"#78350f",lineHeight:1.8}}>{WHY}</div>
        </div>
      </div>

      {/* WHAT — the gallery itself */}
      <div className="rlp-section-pad" style={{maxWidth:1000,margin:"0 auto",padding:"0 20px 48px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <span style={{fontSize:9,fontWeight:800,color:GOLD,letterSpacing:2.5,textTransform:"uppercase",
            background:GOLD+"14",padding:"4px 12px",borderRadius:99}}>What</span>
          <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,margin:0}}>
            The report gallery
          </h2>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:14}}>
          {REPORTS_GALLERY.map(r=>(
            <article key={r.name} style={{background:W,borderRadius:14,padding:"20px 18px",border:`1px solid ${B}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <div style={{width:34,height:34,borderRadius:9,background:r.color+"15",
                  border:`1px solid ${r.color}25`,display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:17,flexShrink:0}}>{r.icon}</div>
                <h3 style={{fontSize:12.5,fontWeight:700,color:N,margin:0,lineHeight:1.3}}>{r.name}</h3>
              </div>
              <p style={{fontSize:11,color:MU,lineHeight:1.65,margin:0}}>{r.contains}</p>
            </article>
          ))}
        </div>
      </div>

      {/* HOW */}
      <div className="rlp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 48px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <span style={{fontSize:9,fontWeight:800,color:BLUE,letterSpacing:2.5,textTransform:"uppercase",
            background:"#eff6ff",padding:"4px 12px",borderRadius:99}}>How</span>
          <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,margin:0}}>
            How reports get generated
          </h2>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
          {[
            "Candidate or team completes the relevant assessment(s) \u2014 self-report, cognitive, or multi-rater 360\u00b0",
            "AXIS360 scores against the appropriate norm group and maps results to the report template built for that instrument",
            "The report renders instantly with charts, benchmarks, and pre-written interview questions \u2014 ready to open, print, or share",
          ].map((step,i)=>(
            <div key={i} style={{background:W,border:`1px solid ${B}`,borderRadius:12,padding:"20px 18px"}}>
              <div style={{width:30,height:30,borderRadius:9,background:GOLD,color:W,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,marginBottom:12}}>
                {i+1}
              </div>
              <div style={{fontSize:13,color:SL,lineHeight:1.65}}>{step}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{background:`linear-gradient(135deg,${N},${N}dd)`,padding:"40px 20px"}}>
        <div style={{maxWidth:700,margin:"0 auto",textAlign:"center"}}>
          <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(22px,5vw,28px)",fontWeight:400,color:W,marginBottom:14}}>
            See these reports on your own data
          </h2>
          <p style={{fontSize:13.5,color:"rgba(255,255,255,.7)",marginBottom:24}}>
            {"Start with a free trial \u2014 no credit card required."}
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={onEnter} className="rlp-cta-btn" style={{background:GOLD,color:N,border:"none",borderRadius:10,
              padding:"13px 28px",fontSize:13.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
              {"Start Free Trial \u2192"}
            </button>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="rlp-cta-btn" style={{background:"rgba(255,255,255,.1)",
              color:W,border:"1.5px solid rgba(255,255,255,.2)",borderRadius:10,padding:"13px 28px",
              fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textDecoration:"none",
              display:"inline-block"}}>
              {"\ud83d\udcac WhatsApp Us"}
            </a>
          </div>
        </div>
      </div>

      <div style={{padding:"20px 16px",textAlign:"center",fontSize:11,color:MU,borderTop:`1px solid ${B}`,wordBreak:"break-word"}}>
        {"AXIS360 by NonVerbals Consulting \u00b7 assess.nonverbals.com \u00b7 nizam@nonverbals.com \u00b7 "}{PHONE_D}
      </div>
    </div>
  );
}
